import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import Breadcrumbs from "../../components/shared/Breadcrumbs.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import { buildBreadcrumbJsonLd } from "../../utils/jsonld.js";
import { ABOUT_PATH, SUBPAGE_PATH } from "./paths.js";
import { SUPPORT_PATH, SUPPORT_SUBPAGE_PATH } from "../Support/paths.js";
import GesellschaftsvertragDe from "./Gesellschaftsvertrag.jsx";
import { VerhaltenskodexDe, VerhaltenskodexEn } from "./Verhaltenskodex.jsx";
import { JugendschutzkonzeptDe, JugendschutzkonzeptEn, JugendschutzAnhaenge } from "./Jugendschutzkonzept.jsx";
import { REPORTS } from "./reports.js";
import { HISTORY } from "./history.js";
import { TEAM } from "./team.js";
import TeamTile from "./TeamTile.jsx";

const CONDUCT_CONTENT = { de: VerhaltenskodexDe, en: VerhaltenskodexEn };
const VOLUNTEER_PATH = SUPPORT_SUBPAGE_PATH.volunteer;

const h2Style = { fontFamily: "var(--font-serif)", fontSize: "24px", color: "var(--color-cream)", margin: 0 };
const bodyStyle = { fontSize: "16px", lineHeight: 1.75, color: "var(--color-cream-soft)", margin: 0 };
const JUGENDSCHUTZ_CONTENT = { de: JugendschutzkonzeptDe, en: JugendschutzkonzeptEn };

/**
 * team/history — still placeholder content only (no fabricated bios or
 * historical claims about the real association; same treatment as
 * Datenschutz/AGB). charter/Gesellschaftsvertrag has real content — see
 * Gesellschaftsvertrag.jsx. reports/Rechenschaftsberichte reads from
 * reports.js, currently empty — no reports exist yet.
 */
export default function AboutSubpage({ lang, topic }) {
  const { t } = useTranslation();
  const otherLang = lang === "de" ? "en" : "de";
  const path = `/${ABOUT_PATH[lang]}/${SUBPAGE_PATH[topic][lang]}`;
  const altPath = `/${ABOUT_PATH[otherLang]}/${SUBPAGE_PATH[topic][otherLang]}`;
  const breadcrumbItems = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.ueberUns"), path: `/${ABOUT_PATH[lang]}` },
    { name: t(`about.${topic}.title`), path },
  ];

  return (
    <>
      <SEOHead
        lang={lang}
        path={path}
        altPath={altPath}
        // Every indexable About subpage carries its own hand-written
        // metaTitle/metaDescription (§ T-07, T-08) — `defaultValue` keeps
        // the noindexed ones (charter) working off their plain title
        // without needing SEO copy they will never show a searcher.
        title={t(`about.${topic}.metaTitle`, { defaultValue: t(`about.${topic}.title`) })}
        description={t(`about.${topic}.metaDescription`, { defaultValue: undefined })}
        // team/conduct/reports are real, citable trust content (who's
        // behind the org, its code of conduct, its financial reporting) —
        // exactly what a parent, sponsor, or partner org evaluates before
        // getting involved, same reasoning as jugendschutz already being
        // indexable — as is history, now that it carries the founding
        // story rather than the same four-line timeline AboutHub shows
        // (§ T-27). Only charter stays noindexed: a German-only notarized
        // legal document (see charter's own EN fallback copy).
        noindex={!["jugendschutz", "team", "conduct", "reports", "history"].includes(topic)}
      />
      <JsonLd data={buildBreadcrumbJsonLd(breadcrumbItems, lang)} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "32px", paddingTop: "32px", paddingBottom: "96px", maxWidth: "820px" }}>
          <Breadcrumbs items={breadcrumbItems} lang={lang} />
          <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "34px", color: "var(--color-cream)", margin: 0 }}>
            {topic === "jugendschutz" ? t("about.jugendschutz.h1") : t(`about.${topic}.title`)}
          </h1>

          {topic === "team" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <p style={{ fontSize: "17px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: 0 }}>{t("about.team.placeholder")}</p>
                <p style={{ fontSize: "16px", lineHeight: 1.75, color: "var(--color-cream-soft)", margin: 0 }}>{t("about.team.intro")}</p>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: "20px" }}>
                {TEAM[lang].map((member, i) => (
                  <TeamTile key={i} member={member} rolePlaceholder={t("about.team.rolePlaceholder")} />
                ))}
              </div>
              <p style={{ fontSize: "14px", lineHeight: 1.7, color: "var(--color-cream-faint)", margin: 0 }}>{t("about.team.photoNote")}</p>

              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <h2 style={h2Style}>{t("about.team.howWeWorkTitle")}</h2>
                <p style={bodyStyle}>{t("about.team.howWeWorkBody")}</p>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <h2 style={h2Style}>{t("about.team.joinTitle")}</h2>
                <p style={bodyStyle}>{t("about.team.joinBody")}</p>
                <Link to={`/${lang}/${SUPPORT_PATH[lang]}/${VOLUNTEER_PATH[lang]}`} style={{ fontSize: "15px", color: "var(--color-gold)" }}>
                  {t("about.team.joinCta")}
                </Link>
              </div>
            </div>
          )}

          {topic === "history" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <p style={bodyStyle}>{t("about.history.intro")}</p>
              {HISTORY[lang].map((entry) => (
                <section
                  key={`${entry.date}-${entry.text}`}
                  style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "24px 0", borderTop: "1px solid rgba(254,250,239,0.1)" }}
                >
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: "13px", color: "var(--color-gold)" }}>{entry.date}</span>
                  <h2 style={h2Style}>{entry.text}</h2>
                  <p style={{ ...bodyStyle, maxWidth: "68ch" }}>{entry.body}</p>
                </section>
              ))}
              <div style={{ display: "flex", flexDirection: "column", gap: "12px", borderTop: "1px solid rgba(254,250,239,0.1)", paddingTop: "24px" }}>
                <h2 style={h2Style}>{t("about.history.outlookTitle")}</h2>
                <p style={{ ...bodyStyle, maxWidth: "68ch" }}>{t("about.history.outlookBody")}</p>
                <Link to={`/${lang}/events`} style={{ fontSize: "15px", color: "var(--color-gold)", alignSelf: "flex-start" }}>
                  {t("about.history.outlookCta")}
                </Link>
              </div>
            </div>
          )}

          {topic === "charter" && lang === "de" && <GesellschaftsvertragDe />}

          {topic === "charter" && lang === "en" && (
            <div className="tl-card-light tl-simple-card" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <p style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.85)", margin: 0 }}>
                Tales&amp;Legends gemeinnützige UG (haftungsbeschränkt) is a German legal entity, and its
                Gesellschaftsvertrag (the notarized founding contract filed with the Amtsgericht Dresden commercial
                register, HRB 48120) is a formal German-language legal document. To avoid any translation
                inaccuracy in a binding legal text, it is published in German only — see the German version of
                this page (use the language switcher above).
              </p>
            </div>
          )}

          {topic === "conduct" &&
            (() => {
              const ConductBody = CONDUCT_CONTENT[lang];
              return <ConductBody />;
            })()}

          {topic === "jugendschutz" &&
            (() => {
              const JugendschutzBody = JUGENDSCHUTZ_CONTENT[lang];
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
                  <JugendschutzBody />
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <p style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "18px", color: "var(--color-cream)", margin: 0 }}>
                      {lang === "de" ? "Vorlagen zum Download: Anhänge A–E" : "Templates to download: Appendices A–E"}
                    </p>
                    <JugendschutzAnhaenge lang={lang} />
                  </div>
                </div>
              );
            })()}

          {topic === "reports" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "28px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <h2 style={h2Style}>{t("about.reports.whyTitle")}</h2>
                <p style={bodyStyle}>{t("about.reports.whyBody")}</p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <h2 style={h2Style}>{t("about.reports.containsTitle")}</h2>
                <p style={bodyStyle}>{t("about.reports.containsBody")}</p>
              </div>
              <p style={bodyStyle}>{t("about.reports.sourcesNote")}</p>
              <p style={{ ...bodyStyle, fontSize: "15px" }}>{t("about.reports.taxNote")}</p>
            </div>
          )}

          {topic === "reports" && (
            <div className="tl-card-light tl-simple-card" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {REPORTS[lang].length === 0 ? (
                <p style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.7)", margin: 0 }}>
                  {t("about.reports.empty")}
                </p>
              ) : (
                <ul style={{ display: "flex", flexDirection: "column", gap: "16px", margin: 0, padding: 0, listStyle: "none" }}>
                  {REPORTS[lang].map((report) => (
                    <li key={report.file}>
                      <a href={report.file} target="_blank" rel="noopener noreferrer" style={{ fontSize: "16px" }}>
                        {report.title}
                      </a>
                      {report.description && (
                        <p style={{ fontSize: "16px", lineHeight: 1.6, color: "rgba(10,9,7,0.65)", margin: "4px 0 0" }}>
                          {report.description}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
